import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Skeleton,
  Alert,
  Grid,
  Input,
  Select,
  Option,
  IconButton,
  FormControl,
  FormLabel,
  Table,
  Sheet,
  Modal,
  ModalDialog,
  ModalClose,
  Divider,
  Snackbar,
} from '@mui/joy';
import { Container } from '@mui/material';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useAuth } from '../../main/context/AuthContext';
import { useSiteSettings } from '../../main/context/SiteSettingsContext';
import { getSiteDisplayName } from '../../main/utils/siteDisplayName';
import { getCustomerShipments, bulkUpdateReceiverDetails, exportCustomerShipmentsToExcel } from '../../main/API/customer';
import { useNavigate } from 'react-router-dom';
import { formatPaymentStatus, getPaymentStatusChipStyle, getPaymentStatusIconConfig } from '../../main/widgets/PaymentStatusOptionList';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DirectionalBackIcon from '../../main/components/DirectionalBackIcon';
import { OmrAmount } from '../../main/components/OmrAmount';
import OmrSymbol from '../../main/components/OmrSymbol';
import { translateApiErrorData } from '../../main/utils/masterApiError';
import { translateAggregateBackendMessage } from '../../main/utils/backendErrorTranslation';
import { dedupeRequest } from '../../main/utils/dedupeRequest';
import { getPaginatedMeta } from '../../main/utils/pagination';
import CustomerDeliveryDestinationForm from './widgets/CustomerDeliveryDestinationForm';
import { formatCustomerDestinationTypeLabel } from '../utils/customerDestinationTypes';
import {
  formatCustomerShipmentUnitDate,
  getCustomerShipmentUnit,
  getCustomerShipmentUnitSerial,
  isCustomerShipmentUnitFieldVisible,
  useCustomerShipmentUnitVisibleFields,
} from '../utils/customerShipmentUnit';
import { eventTypeDisplayFromRow } from '../../main/utils/trackingEventLabels';

function formatShipmentStatus(status, t) {
  if (!status) return '—';
  const key = String(status).toLowerCase().trim().replace(/\s+/g, '_');
  const map = {
    shipping_soon: 'customer.shipmentSoon',
    shipped: 'customer.shipmentShipped',
    arrived: 'customer.shipmentArrived',
    out_for_delivery: 'customer.shipmentOutForDelivery',
    delivered: 'customer.shipmentDelivered',
  };
  if (map[key]) return t(map[key]);
  return status;
}

function shipmentStatusChipStyle(status) {
  const styles = {
    shipping_soon: { bg: '#f1f5f9', color: '#64748b' },
    'shipping soon': { bg: '#f1f5f9', color: '#64748b' },
    shipped: { bg: '#eef2ff', color: '#4f46e5' },
    arrived: { bg: '#eff6ff', color: '#2563eb' },
    out_for_delivery: { bg: '#fffbeb', color: '#d97706' },
    'out for delivery': { bg: '#fffbeb', color: '#d97706' },
    delivered: { bg: '#ecfdf5', color: '#059669' },
  };
  const key = status && String(status).toLowerCase().trim().replace(/\s+/g, '_');
  const withSpaces = key && key.replace(/_/g, ' ');
  return styles[key] || styles[withSpaces] || { bg: '#f1f5f9', color: '#64748b' };
}

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_) {
    return '—';
  }
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '—';
  }
}

function formatDestinationLocationType(value, t, siteName) {
  return formatCustomerDestinationTypeLabel(value, t, siteName);
}

/** True if delivery location cannot be updated (out for delivery, delivered). */
function cannotUpdateDeliveryLocation(status) {
  if (!status) return false;
  const key = String(status).toLowerCase().trim().replace(/\s+/g, '_');
  const normalized = key.replace(/_/g, ' ');
  return ['out for delivery', 'delivered'].includes(normalized) || ['out_for_delivery', 'delivered'].includes(key);
}

function getShipmentLocationLabel(shipment) {
  if (!shipment) return null;
  if (shipment.location_name && String(shipment.location_name).trim()) {
    return String(shipment.location_name).trim();
  }
  if (shipment.current_location && String(shipment.current_location).trim()) {
    return String(shipment.current_location).trim();
  }
  const loc = shipment.location;
  if (loc != null && loc !== '') {
    if (typeof loc === 'object' && loc.name) return String(loc.name).trim();
    return String(loc).trim();
  }
  return null;
}

function eventTypeChipStyle(code) {
  const styles = {
    delivered: { bg: '#ecfdf5', color: '#059669' },
    out_for_delivery: { bg: '#fffbeb', color: '#d97706' },
    in_transit: { bg: '#eef2ff', color: '#4f46e5' },
    arrived_destination_port: { bg: '#eff6ff', color: '#2563eb' },
    arrived_destination_airport: { bg: '#eff6ff', color: '#2563eb' },
    customs_cleared_destination: { bg: '#f0fdfa', color: '#0d9488' },
    at_dc_destination: { bg: '#f1f5f9', color: '#475569' },
    at_warehouse_origin: { bg: '#f1f5f9', color: '#475569' },
    picked_up_origin: { bg: '#f5f3ff', color: '#7c3aed' },
    order_confirmed: { bg: '#f8fafc', color: '#64748b' },
    awaiting_pickup: { bg: '#f8fafc', color: '#64748b' },
  };
  const key = code && String(code).toLowerCase().trim();
  if (key && styles[key]) return styles[key];
  if (key && (key.includes('departed') || key.includes('arrived'))) {
    return { bg: '#eff6ff', color: '#2563eb' };
  }
  return { bg: '#f1f5f9', color: '#64748b' };
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const initialFilters = {
  tracking_number: '',
  shipper_reference: '',
  items_description: '',
  note: '',
  status: '',
  payment_status: '',
  event_type: '',
  id_in: '',
  packages_quantity: '',
  packages_quantity_min: '',
  packages_quantity_max: '',
  weight_min: '',
  weight_max: '',
  width_min: '',
  width_max: '',
  length_min: '',
  length_max: '',
  height_min: '',
  height_max: '',
  volumetric_weight_min: '',
  volumetric_weight_max: '',
  price_min: '',
  price_max: '',
  invoiced: '',
  created_at_after: '',
  created_at_before: '',
  modified_at_after: '',
  modified_at_before: '',
  transaction_date: '',
  transaction_date_after: '',
  transaction_date_before: '',
  ordering: '-created_at',
};

function toIsoDateOnly(dateStr) {
  if (!dateStr || !dateStr.trim()) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

const EVENT_TYPE_FILTER_VALUES = [
  'order_confirmed', 'awaiting_pickup', 'picked_up_origin', 'at_warehouse_origin', 'departed_origin_port', 'departed_origin_airport',
  'in_transit', 'arrived_destination_port', 'arrived_destination_airport', 'departed_destination_airport', 'customs_cleared_destination',
  'at_dc_destination', 'out_for_delivery', 'delivered', 'other',
];

export default function CustomerMyShipmentsScreen() {
  const { t, i18n } = useTranslation('ui');
  const { user, logout } = useAuth();
  const tRef = useRef(t);
  const logoutRef = useRef(logout);
  tRef.current = t;
  logoutRef.current = logout;
  const accountInactive = Boolean(user && user.is_active === false);
  const { settings } = useSiteSettings();
  const unitVisibleFields = useCustomerShipmentUnitVisibleFields();
  const siteName = getSiteDisplayName(settings, i18n.language);
  const navigate = useNavigate();

  const statusFilterOptions = useMemo(() => [
    { value: '', label: t('customerList.shipmentStatusAll') },
    { value: 'shipping soon', label: t('customer.shipmentSoon') },
    { value: 'shipped', label: t('customer.shipmentShipped') },
    { value: 'arrived', label: t('customer.shipmentArrived') },
    { value: 'out for delivery', label: t('customer.shipmentOutForDelivery') },
    { value: 'delivered', label: t('customer.shipmentDelivered') },
  ], [t]);

  const paymentStatusOptions = useMemo(() => [
    { value: '', label: t('customerList.paymentAny') },
    { value: 'unpaid', label: t('customerList.paymentUnpaid') },
    { value: 'partially_paid', label: t('customerList.paymentPartiallyPaid') },
    { value: 'paid', label: t('customerList.paymentPaid') },
    { value: 'overpaid', label: t('customerList.paymentOverpaid') },
    { value: 'refunded', label: t('customerList.paymentRefunded') },
  ], [t]);

  const eventTypeOptions = useMemo(() => [
    { value: '', label: t('customerList.eventTypeAny') },
    ...EVENT_TYPE_FILTER_VALUES.map((code) => ({ value: code, label: t(`eventTypeCodes.${code}`) })),
  ], [t]);

  const booleanFilterOptions = useMemo(() => [
    { value: '', label: t('customerList.boolAny') },
    { value: 'true', label: t('customerList.boolYes') },
    { value: 'false', label: t('customerList.boolNo') },
  ], [t]);

  const orderingOptions = useMemo(() => [
    { value: '-created_at', label: t('customerList.orderNewestFirst') },
    { value: 'created_at', label: t('customerList.orderOldestFirst') },
    { value: '-modified_at', label: t('customerList.orderRecentlyUpdated') },
    { value: 'modified_at', label: t('customerList.orderLeastRecentlyUpdated') },
    { value: '-price', label: t('customerList.orderPriceHighToLow') },
    { value: 'price', label: t('customerList.orderPriceLowToHigh') },
    { value: 'tracking_number', label: t('customerList.orderTrackingAZ') },
    { value: '-tracking_number', label: t('customerList.orderTrackingZA') },
    { value: 'status', label: t('customerList.orderStatusAZ') },
    { value: '-status', label: t('customerList.orderStatusZA') },
  ], [t]);

  const [data, setData] = useState({ results: [], count: 0, page_index: 1, page_size: 10 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [quickViewShipment, setQuickViewShipment] = useState(null);
  const [selectedShipmentIds, setSelectedShipmentIds] = useState(new Set());
  const [bulkReceiverModalOpen, setBulkReceiverModalOpen] = useState(false);
  const [bulkDestinationType, setBulkDestinationType] = useState('home');
  const [bulkDestinationLocation, setBulkDestinationLocation] = useState('');
  const [bulkReceiverSubmitting, setBulkReceiverSubmitting] = useState(false);
  const [bulkReceiverResult, setBulkReceiverResult] = useState(null);
  const [bulkSuccessMessage, setBulkSuccessMessage] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [singleDeliveryShipment, setSingleDeliveryShipment] = useState(null);
  const [singleDeliveryType, setSingleDeliveryType] = useState('home');
  const [singleDeliveryLocation, setSingleDeliveryLocation] = useState('');
  const [singleDeliverySubmitting, setSingleDeliverySubmitting] = useState(false);
  const [singleDeliveryResult, setSingleDeliveryResult] = useState(null);

  const buildParams = useCallback(() => {
    const p = { page, page_size: pageSize, ordering: appliedFilters.ordering };
    [
      'tracking_number', 'shipper_reference', 'items_description', 'note',
      'status', 'payment_status', 'event_type', 'id_in',
      'packages_quantity', 'packages_quantity_min', 'packages_quantity_max',
      'weight_min', 'weight_max', 'width_min', 'width_max', 'length_min', 'length_max',
      'height_min', 'height_max', 'volumetric_weight_min', 'volumetric_weight_max',
      'price_min', 'price_max', 'invoiced',
      'created_at_after', 'created_at_before', 'modified_at_after', 'modified_at_before',
      'transaction_date', 'transaction_date_after', 'transaction_date_before',
    ].forEach((key) => {
      const v = appliedFilters[key];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        if (
          key === 'created_at_after' || key === 'created_at_before' || key === 'modified_at_after' || key === 'modified_at_before' ||
          key === 'transaction_date' || key === 'transaction_date_after' || key === 'transaction_date_before'
        ) {
          const dateVal = toIsoDateOnly(v);
          if (dateVal) p[key] = dateVal;
        } else {
          p[key] = v;
        }
      }
    });
    return p;
  }, [page, pageSize, appliedFilters]);

  const listRequestKey = `customer/shipments:${page}:${pageSize}:${JSON.stringify(appliedFilters)}`;

  const fetchShipments = useCallback(async ({ dedupeKey, isCancelled } = {}) => {
    if (isCancelled && isCancelled()) return;
    if (accountInactive) {
      setLoading(false);
      setData({ results: [], count: 0, page_index: 1, page_size: pageSize });
      setError(null);
      return;
    }
    setError(null);
    setLoading(true);
    const params = buildParams();
    const doFetch = () => getCustomerShipments(params);
    const response = dedupeKey
      ? await dedupeRequest(dedupeKey, doFetch)
      : await doFetch();
    if (isCancelled && isCancelled()) return;
    setLoading(false);

    if (!response) {
      setError(tRef.current('customerList.loadShipmentsError'));
      return;
    }
    if (response.status === 401) {
      logoutRef.current();
      return;
    }
    if (response.status >= 400) {
      setError((response.data && translateApiErrorData(response.data, tRef.current)) || tRef.current('customerList.loadShipmentsFailed'));
      setData({ results: [], count: 0, page_index: 1, page_size: pageSize });
      return;
    }

    const body = response.data;
    setData(getPaginatedMeta(body, pageSize));
  }, [accountInactive, buildParams, pageSize]);

  const fetchShipmentsRef = useRef(fetchShipments);
  fetchShipmentsRef.current = fetchShipments;

  useEffect(() => {
    let cancelled = false;
    fetchShipmentsRef.current({ dedupeKey: listRequestKey, isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRequestKey, accountInactive]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(1);
  };

  const hasActiveFilters = Object.keys(initialFilters).some((k) => {
    if (k === 'ordering') return false;
    const v = appliedFilters[k];
    return v !== undefined && v !== null && String(v).trim() !== '';
  });
  const clearAllFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(1);
  };

  const pageIds = (data.results || []).map((s) => s.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedShipmentIds.has(id));
  const selectedOnPage = (data.results || []).filter((s) => selectedShipmentIds.has(s.id));
  const hasNonUpdatableInSelection = selectedOnPage.some((s) => cannotUpdateDeliveryLocation(s.status));
  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedShipmentIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedShipmentIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };
  const toggleSelectShipment = (id) => {
    setSelectedShipmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const openBulkReceiverModal = () => {
    setBulkReceiverResult(null);
    setBulkDestinationType('home');
    setBulkDestinationLocation('');
    setBulkReceiverModalOpen(true);
  };

  const handleExportToExcel = async () => {
    setExportError(null);
    setExportLoading(true);
    try {
      const hasSelection = selectedShipmentIds.size > 0;
      let params;
      if (hasSelection) {
        params = { shipment_ids: Array.from(selectedShipmentIds) };
      } else {
        params = buildParams();
      }
      const response = await exportCustomerShipmentsToExcel(params);
      setExportLoading(false);
      if (!response) {
        setExportError(t('customerList.exportFailed'));
        return;
      }
      if (response.status === 401) {
        logout();
        return;
      }
      if (response.status >= 400) {
        let msg = t('customerList.exportFailedShort');
        if (response.data instanceof Blob) {
          try {
            const text = await response.data.text();
            const json = JSON.parse(text);
            msg = translateApiErrorData(json, t) || msg;
          } catch (_) {
            // keep default msg
          }
        } else if (response.data) {
          msg = translateApiErrorData(response.data, t) || msg;
        }
        setExportError(msg);
        return;
      }
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
      link.download = `my_shipments_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      setExportError(null);
      setBulkSuccessMessage(t('customerList.exportSuccess'));
    } catch (err) {
      setExportLoading(false);
      setExportError(err.message || t('customerList.exportFailedShort'));
    }
  };
  const openSingleDeliveryModal = (shipment) => {
    setSingleDeliveryShipment(shipment);
    setSingleDeliveryType(shipment.receiver_details && shipment.receiver_details.destination_location_type ? String(shipment.receiver_details.destination_location_type).toLowerCase() : 'home');
    setSingleDeliveryLocation(shipment.receiver_details && shipment.receiver_details.destination_location ? String(shipment.receiver_details.destination_location) : '');
    setSingleDeliveryResult(null);
  };
  const handleSingleDeliverySubmit = async () => {
    if (!singleDeliveryShipment) return;
    setSingleDeliverySubmitting(true);
    setSingleDeliveryResult(null);
    const response = await bulkUpdateReceiverDetails([singleDeliveryShipment.id], singleDeliveryType, singleDeliveryLocation);
    setSingleDeliverySubmitting(false);
    if (!response) {
      setSingleDeliveryResult({ error: t('customer.requestFailed') });
      return;
    }
    if (response.status === 401) {
      setSingleDeliveryShipment(null);
      logout();
      return;
    }
    if (response.status === 400) {
      const data = response.data || {};
      const raw = Object.entries(data)
        .map(([, v]) => (Array.isArray(v) ? v.join(' ') : String(v)))
        .join(' ');
      const messages = raw ? translateAggregateBackendMessage(raw, t) : '';
      setSingleDeliveryResult({ error: messages || t('customer.validationError') });
      return;
    }
    if (response.status === 200) {
      setBulkSuccessMessage(t('customer.deliveryLocationUpdated'));
      setSingleDeliveryShipment(null);
      setSingleDeliveryResult(null);
      fetchShipments();
    } else {
      setSingleDeliveryResult({
        error: (response.data && translateApiErrorData(response.data, t)) || t('customerList.updateFailed'),
      });
    }
  };
  const handleBulkReceiverSubmit = async () => {
    const ids = Array.from(selectedShipmentIds);
    if (ids.length === 0) return;
    const idsToSend = ids.filter((id) => {
      const s = (data.results || []).find((r) => r.id === id);
      if (!s) return true;
      return !cannotUpdateDeliveryLocation(s.status);
    });
    if (idsToSend.length === 0) {
      setBulkReceiverResult({ error: t('customerList.bulkNoneEligible') });
      return;
    }
    setBulkReceiverSubmitting(true);
    setBulkReceiverResult(null);
    const response = await bulkUpdateReceiverDetails(idsToSend, bulkDestinationType, bulkDestinationLocation);
    setBulkReceiverSubmitting(false);
    if (!response) {
      setBulkReceiverResult({ error: t('customer.requestFailed') });
      return;
    }
    if (response.status === 401) {
      setBulkReceiverModalOpen(false);
      logout();
      return;
    }
    if (response.status === 400) {
      const data = response.data || {};
      const raw = Object.entries(data)
        .map(([, v]) => (Array.isArray(v) ? v.join(' ') : String(v)))
        .join(' ');
      const messages = raw ? translateAggregateBackendMessage(raw, t) : '';
      setBulkReceiverResult({ error: messages || t('customer.validationError') });
      return;
    }
    if (response.status === 200) {
      const d = response.data || {};
      const notFound = d.shipment_ids_not_found_or_not_owned || [];
      const skippedStatus = d.shipment_ids_skipped_due_to_status || [];
      const msg = d.message || t('customerList.bulkUpdatedCount', { count: d.updated_count != null ? d.updated_count : 0 });
      const parts = [msg];
      if (notFound.length > 0) {
        parts.push(t('customerList.bulkIdsNotFoundSkipped', { count: notFound.length }));
      }
      if (skippedStatus.length > 0) {
        parts.push(t('customerList.bulkSkippedDueToStatus', { count: skippedStatus.length }));
      }
      setBulkSuccessMessage(parts.join(' '));
      setSelectedShipmentIds(new Set());
      setBulkReceiverModalOpen(false);
      setBulkReceiverResult(null);
      fetchShipments();
    } else {
      setBulkReceiverResult({
        error: (response.data && translateApiErrorData(response.data, t)) || t('customerList.updateFailed'),
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.count || 0) / pageSize));
  const pageBg = '#f0f4f8';
  const cardBg = '#ffffff';
  const cardBorder = '1px solid #e2e8f0';
  const cardShadow = '0 2px 8px rgba(0,0,0,0.06)';
  const cardRadius = '16px';
  const textPrimary = '#1e293b';
  const textSecondary = '#64748b';
  const textMuted = '#94a3b8';

  if (loading && !data.results.length) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
        <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: cardRadius, mb: 3, bgcolor: cardBg }} />
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: cardRadius, mb: 2, bgcolor: cardBg }} />
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Grid key={i} xs={12} sm={6} md={4}>
                  <Skeleton variant="rectangular" height={180} sx={{ borderRadius: cardRadius, bgcolor: cardBg }} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
      <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="plain"
                size="sm"
                startDecorator={<DirectionalBackIcon />}
                onClick={() => navigate('/customer-home')}
                sx={{ color: textSecondary, fontWeight: 500, mr: 0.5 }}
              >
                {t('customerList.homeNav')}
              </Button>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LocalShippingIcon sx={{ color: '#4f46e5', fontSize: 22 }} />
              </Box>
              <Typography level="h4" sx={{ fontWeight: 700, color: textPrimary }}>
                {t('customerList.myShipmentsTitle')}
              </Typography>
            </Box>
            <Button
              variant="soft"
              size="sm"
              startDecorator={<RefreshIcon />}
              onClick={fetchShipments}
              loading={loading}
              sx={{ borderRadius: '10px', fontWeight: 500 }}
            >
              {t('actions.refresh')}
            </Button>
          </Box>

          {error && (
            <Alert
              color="danger"
              variant="soft"
              sx={{ mb: 3, borderRadius: '12px' }}
              endDecorator={
                <Button variant="soft" color="danger" size="sm" onClick={fetchShipments}>
                  {t('actions.retry')}
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          <Card variant="plain" sx={{ bgcolor: cardBg, boxShadow: cardShadow, borderRadius: cardRadius, border: cardBorder, mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              {/* Quick filters row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', mb: showAllFilters ? 2 : 0 }}>
                <Input
                  placeholder={t('customerList.trackingNumberPlaceholder')}
                  value={filters.tracking_number}
                  onChange={(e) => updateFilter('tracking_number', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  size="md"
                  sx={{ minWidth: 160, borderRadius: '10px' }}
                />
                <Input
                  placeholder={t('customerList.shipperReferencePlaceholder')}
                  value={filters.shipper_reference}
                  onChange={(e) => updateFilter('shipper_reference', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  size="md"
                  sx={{ minWidth: 160, borderRadius: '10px' }}
                />
                <Select
                  size="md"
                  value={filters.status}
                  onChange={(_, v) => updateFilter('status', v || '')}
                  sx={{ minWidth: 150, borderRadius: '10px' }}
                >
                  {statusFilterOptions.map((opt) => (
                    <Option key={opt.value || 'all'} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
                <Select
                  size="md"
                  value={filters.payment_status}
                  onChange={(_, v) => updateFilter('payment_status', v || '')}
                  sx={{ minWidth: 160, borderRadius: '10px' }}
                  renderValue={(option) => {
                    if (!filters.payment_status) return (option && option.label) || t('customerList.paymentAny');
                    const config = getPaymentStatusIconConfig(filters.payment_status);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MonetizationOnIcon
                          color={config.color === 'success' ? 'success' : undefined}
                          sx={config.color !== 'success' ? { color: config.color, fontSize: '18px' } : { fontSize: '18px' }}
                        />
                        <span>{config.title}</span>
                      </Box>
                    );
                  }}
                >
                  {paymentStatusOptions.map((opt) => {
                    if (!opt.value) {
                      return <Option key="any" value={opt.value}>{opt.label}</Option>;
                    }
                    const config = getPaymentStatusIconConfig(opt.value);
                    return (
                      <Option key={opt.value} value={opt.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MonetizationOnIcon
                            color={config.color === 'success' ? 'success' : undefined}
                            sx={config.color !== 'success' ? { color: config.color, fontSize: '18px' } : { fontSize: '18px' }}
                          />
                          <span>{config.title}</span>
                        </Box>
                      </Option>
                    );
                  })}
                </Select>
                <Select
                  size="md"
                  value={filters.ordering}
                  onChange={(_, v) => updateFilter('ordering', v || '-created_at')}
                  sx={{ minWidth: 160, borderRadius: '10px' }}
                >
                  {orderingOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
                <Button size="md" onClick={handleApplyFilters} startDecorator={<SearchIcon />} sx={{ borderRadius: '10px' }}>
                  {t('actions.apply')}
                </Button>
                {hasActiveFilters && (
                  <Button variant="plain" size="md" onClick={clearAllFilters}>
                    {t('customerList.clearAll')}
                  </Button>
                )}
                <Button
                  variant="soft"
                  size="md"
                  color="neutral"
                  onClick={() => setShowAllFilters((s) => !s)}
                  endDecorator={showAllFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  startDecorator={<FilterListIcon />}
                  sx={{ borderRadius: '10px' }}
                >
                  {showAllFilters ? t('customerList.lessFilters') : t('customerList.allFilters')}
                </Button>
                <Button
                  variant="soft"
                  size="md"
                  startDecorator={<FileDownloadIcon />}
                  onClick={handleExportToExcel}
                  loading={exportLoading}
                  disabled={exportLoading}
                  sx={{ borderRadius: '10px', fontWeight: 500 }}
                >
                  {selectedShipmentIds.size > 0
                    ? t('customerList.exportExcelSelected', { count: selectedShipmentIds.size })
                    : t('customerList.exportExcel')}
                </Button>
              </Box>

              {/* All filters (expandable) */}
              {showAllFilters && (
                <Box sx={{ pt: 2, borderTop: '1px solid #e2e8f0' }}>
                  <Grid container spacing={2}>
                    <Grid xs={12}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.textContainsSection')}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                      <FormControl size="sm">
                        <FormLabel>{t('customer.quickView.itemsDescription')}</FormLabel>
                        <Input
                          placeholder={t('customer.quickView.itemsDescription')}
                          value={filters.items_description}
                          onChange={(e) => updateFilter('items_description', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.colNote')}</FormLabel>
                        <Input
                          placeholder={t('customerList.notePlaceholder')}
                          value={filters.note}
                          onChange={(e) => updateFilter('note', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid xs={12} sx={{ mt: 1 }}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.identity')}</Typography>
                    </Grid>
                    <Grid xs={12} sm={12} md={8}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.shipmentIdsComma')}</FormLabel>
                        <Input
                          placeholder={t('customerList.idInPlaceholder')}
                          value={filters.id_in}
                          onChange={(e) => updateFilter('id_in', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid xs={12} sx={{ mt: 1 }}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.numericRangesSection')}</Typography>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.packagesExact')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.packages_quantity}
                          onChange={(e) => updateFilter('packages_quantity', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.packagesMin')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.packages_quantity_min}
                          onChange={(e) => updateFilter('packages_quantity_min', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.packagesMax')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.packages_quantity_max}
                          onChange={(e) => updateFilter('packages_quantity_max', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.weightMin')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.weight_min}
                          onChange={(e) => updateFilter('weight_min', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.weightMax')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.weight_max}
                          onChange={(e) => updateFilter('weight_max', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <span>{t('customerList.priceMinOmr')}</span>
                          <OmrSymbol height={12} />
                        </FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.price_min}
                          onChange={(e) => updateFilter('price_min', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <span>{t('customerList.priceMaxOmr')}</span>
                          <OmrSymbol height={12} />
                        </FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.price_max}
                          onChange={(e) => updateFilter('price_max', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.volWeightMin')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.volumetric_weight_min}
                          onChange={(e) => updateFilter('volumetric_weight_min', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.volWeightMax')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.volumetric_weight_max}
                          onChange={(e) => updateFilter('volumetric_weight_max', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.widthMin')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.width_min}
                          onChange={(e) => updateFilter('width_min', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.widthMax')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.width_max}
                          onChange={(e) => updateFilter('width_max', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.lengthMin')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.length_min}
                          onChange={(e) => updateFilter('length_min', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.lengthMax')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.length_max}
                          onChange={(e) => updateFilter('length_max', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.heightMin')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.height_min}
                          onChange={(e) => updateFilter('height_min', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={6} sm={4} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.heightMax')}</FormLabel>
                        <Input
                          type="number"
                          placeholder="—"
                          value={filters.height_max}
                          onChange={(e) => updateFilter('height_max', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid xs={12} sx={{ mt: 1 }}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.eventTypeBooleanSection')}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                      <FormControl size="sm">
                        <FormLabel>{t('customer.quickView.eventType')}</FormLabel>
                        <Select
                          value={filters.event_type}
                          onChange={(_, v) => updateFilter('event_type', v || '')}
                          sx={{ borderRadius: '8px' }}
                        >
                          {eventTypeOptions.map((opt) => (
                            <Option key={opt.value || 'any'} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customer.quickView.invoiced')}</FormLabel>
                        <Select
                          value={filters.invoiced}
                          onChange={(_, v) => updateFilter('invoiced', v || '')}
                          sx={{ borderRadius: '8px' }}
                        >
                          {booleanFilterOptions.map((opt) => (
                            <Option key={opt.value || 'any'} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid xs={12} sx={{ mt: 1 }}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.dateRangeCreatedModified')}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.createdAfter')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.created_at_after}
                          onChange={(e) => updateFilter('created_at_after', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.createdBefore')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.created_at_before}
                          onChange={(e) => updateFilter('created_at_before', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.modifiedAfter')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.modified_at_after}
                          onChange={(e) => updateFilter('modified_at_after', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.modifiedBefore')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.modified_at_before}
                          onChange={(e) => updateFilter('modified_at_before', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid xs={12} sx={{ mt: 1 }}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.transactionDateShipmentSection')}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.exactDate')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.transaction_date}
                          onChange={(e) => updateFilter('transaction_date', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.onOrAfter')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.transaction_date_after}
                          onChange={(e) => updateFilter('transaction_date_after', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.onOrBefore')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.transaction_date_before}
                          onChange={(e) => updateFilter('transaction_date_before', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button size="sm" onClick={handleApplyFilters}>
                      {t('customerList.applyFilters')}
                    </Button>
                    <Button variant="plain" size="sm" onClick={clearAllFilters}>
                      {t('customerList.clearAll')}
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography level="body2" sx={{ color: textSecondary }}>
              {data.count === 0 ? t('customerList.noShipments') : t('customerList.totalShipments', { count: data.count })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="body2" sx={{ color: textSecondary }}>{t('customerList.perPage')}</Typography>
              <Select
                size="sm"
                value={pageSize}
                onChange={(_, v) => {
                  setPageSize(v || 10);
                  setPage(1);
                }}
                sx={{ width: 70 }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <Option key={n} value={n}>{n}</Option>
                ))}
              </Select>
            </Box>
          </Box>

          {selectedShipmentIds.size > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mb: 2,
                p: 1.5,
                borderRadius: '12px',
                bgcolor: '#eef2ff',
                border: '1px solid #c7d2fe',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography level="body1" sx={{ fontWeight: 600, color: textPrimary }}>
                  {t('customerList.shipmentsSelected', { count: selectedShipmentIds.size })}
                </Typography>
                <Button
                  variant="soft"
                  color="primary"
                  size="sm"
                  startDecorator={<LocationOnIcon />}
                  onClick={openBulkReceiverModal}
                  disabled={hasNonUpdatableInSelection}
                  title={hasNonUpdatableInSelection ? t('customerList.bulkDeliveryDisabledTitle') : undefined}
                  sx={{ borderRadius: '10px', fontWeight: 500 }}
                >
                  {t('customer.delivery')}
                </Button>
                <Button
                  variant="plain"
                  size="sm"
                  onClick={() => setSelectedShipmentIds(new Set())}
                  sx={{ fontWeight: 500 }}
                >
                  {t('customerList.clearSelection')}
                </Button>
              </Box>
              {hasNonUpdatableInSelection && (
                <Alert
                  size="sm"
                  color="neutral"
                  variant="soft"
                  startDecorator={<InfoOutlinedIcon />}
                  sx={{
                    alignItems: 'center',
                    py: 0.5,
                    px: 1.5,
                    borderRadius: '8px',
                    '& .JoyAlert-message': { fontSize: '0.8125rem' },
                  }}
                >
                  {t('customerList.bulkCannotUpdateAlert')}
                </Alert>
              )}
            </Box>
          )}

          {!data.results.length ? (
            <Card variant="plain" sx={{ bgcolor: cardBg, borderRadius: cardRadius, boxShadow: cardShadow, border: cardBorder }}>
              <CardContent sx={{ py: 6, px: 2, textAlign: 'center' }}>
                <LocalShippingIcon sx={{ fontSize: 48, color: textMuted, opacity: 0.6, mb: 1.5 }} />
                <Typography level="body1" sx={{ color: textSecondary }}>
                  {t('customerList.noShipmentsFound')}
                </Typography>
                {hasActiveFilters && (
                  <Button variant="soft" size="sm" sx={{ mt: 2 }} onClick={clearAllFilters}>
                    {t('customerList.clearFiltersBtn')}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <Sheet
                variant="outlined"
                sx={{
                  borderRadius: cardRadius,
                  border: cardBorder,
                  boxShadow: cardShadow,
                  overflow: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  '& thead th': { fontWeight: 600, color: textSecondary, fontSize: '0.8125rem', whiteSpace: 'nowrap' },
                  '& tbody tr:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  '& td': { verticalAlign: 'middle', fontSize: '0.875rem' },
                  '& thead th:first-of-type': {
                    position: 'sticky',
                    top: 0,
                    insetInlineStart: 0,
                    zIndex: 12,
                    minWidth: 48,
                    width: 48,
                    backgroundColor: cardBg,
                    backgroundClip: 'padding-box',
                    boxShadow: '4px 0 8px rgba(0,0,0,0.08)',
                    pointerEvents: 'auto',
                    isolation: 'isolate',
                  },
                  'html[dir="rtl"] & thead th:first-of-type': {
                    boxShadow: '-4px 0 8px rgba(0,0,0,0.08)',
                  },
                  '& tbody td:first-of-type': {
                    position: 'sticky',
                    insetInlineStart: 0,
                    zIndex: 11,
                    backgroundColor: cardBg,
                    backgroundClip: 'padding-box',
                    boxShadow: '4px 0 8px rgba(0,0,0,0.06)',
                    pointerEvents: 'auto',
                    isolation: 'isolate',
                  },
                  'html[dir="rtl"] & tbody td:first-of-type': {
                    boxShadow: '-4px 0 8px rgba(0,0,0,0.06)',
                  },
                  '& tbody tr:hover td:first-of-type': {
                    backgroundColor: 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                <Table stickyHeader hoverRow size="md" sx={{ minWidth: 2390, borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}>
                        {data.results.length > 0 && (
                          <NativeCheckbox
                            checked={allOnPageSelected}
                            indeterminate={selectedShipmentIds.size > 0 && !allOnPageSelected}
                            onChange={toggleSelectAll}
                            sx={{ verticalAlign: 'middle' }}
                          />
                        )}
                      </th>
                      <th style={{ minWidth: 72, width: 72 }}>{t('customerList.colId')}</th>
                      <th style={{ minWidth: 130 }}>{t('customerList.colShipperRef')}</th>
                      <th style={{ minWidth: 150 }}>{t('customerList.colTracking')}</th>
                      {isCustomerShipmentUnitFieldVisible('serial_number', unitVisibleFields) && (
                      <th style={{ minWidth: 120 }}>{t('customerList.colUnitSerial')}</th>
                      )}
                      {isCustomerShipmentUnitFieldVisible('expected_destination_eta', unitVisibleFields) && (
                      <th style={{ minWidth: 165 }}>{t('customerList.colExpectedDestinationEta')}</th>
                      )}
                      {isCustomerShipmentUnitFieldVisible('expected_warehouse_eta', unitVisibleFields) && (
                      <th style={{ minWidth: 165 }}>{t('customerList.colExpectedWarehouseEta')}</th>
                      )}
                      <th style={{ minWidth: 200 }}>{t('customerList.colDescription')}</th>
                      <th style={{ minWidth: 200 }}>{t('customerList.colLocation')} / {t('customer.quickView.eventType')}</th>
                      <th style={{ minWidth: 115 }}>{t('customerList.colDestType')}</th>
                      <th style={{ minWidth: 180 }}>{t('customerList.colDestination')}</th>
                      <th style={{ minWidth: 135 }}>{t('customerList.colStatus')}</th>
                      <th style={{ minWidth: 125 }}>{t('customerList.colPayment')}</th>
                      <th style={{ minWidth: 112 }}>{t('customerList.colPrice')}</th>
                      <th style={{ minWidth: 120 }}>{t('customerList.colTransDate')}</th>
                      <th style={{ minWidth: 115 }}>{t('customerList.colCreated')}</th>
                      <th style={{ minWidth: 250, width: 250 }}>{t('customerList.colAction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.results.map((s) => {
                      const statusStyle = shipmentStatusChipStyle(s.status);
                      const locationLabel = getShipmentLocationLabel(s);
                      const eventLabel = eventTypeDisplayFromRow(s, eventTypeOptions);
                      const hasEvent = Boolean(s.event_type && eventLabel && eventLabel !== '—');
                      const eventStyle = hasEvent ? eventTypeChipStyle(s.event_type) : null;
                      const unit = getCustomerShipmentUnit(s);
                      return (
                        <tr key={s.id}>
                          <td>
                            <NativeCheckbox
                              checked={selectedShipmentIds.has(s.id)}
                              onChange={() => toggleSelectShipment(s.id)}
                              sx={{ verticalAlign: 'middle' }}
                            />
                          </td>
                          <td>
                            <Typography level="body2" sx={{ fontWeight: 600, color: textPrimary }}>
                              #{s.id}
                            </Typography>
                          </td>
                          <td>{s.shipper_reference || '—'}</td>
                          <td>{s.tracking_number || '—'}</td>
                          {isCustomerShipmentUnitFieldVisible('serial_number', unitVisibleFields) && (
                          <td>
                            <Typography level="body2" sx={{ maxWidth: 120 }} noWrap title={getCustomerShipmentUnitSerial(s) || ''}>
                              {getCustomerShipmentUnitSerial(s) || '—'}
                            </Typography>
                          </td>
                          )}
                          {isCustomerShipmentUnitFieldVisible('expected_destination_eta', unitVisibleFields) && (
                          <td>{unit?.expected_destination_eta ? formatCustomerShipmentUnitDate(unit.expected_destination_eta) : '—'}</td>
                          )}
                          {isCustomerShipmentUnitFieldVisible('expected_warehouse_eta', unitVisibleFields) && (
                          <td>{unit?.expected_warehouse_eta ? formatCustomerShipmentUnitDate(unit.expected_warehouse_eta) : '—'}</td>
                          )}
                          <td>
                            <Typography level="body2" sx={{ maxWidth: 260 }} noWrap title={s.items_description || ''}>
                              {s.items_description || '—'}
                            </Typography>
                          </td>
                          <td>
                            {!hasEvent && !locationLabel ? (
                              '—'
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 0.25, minWidth: 0, maxWidth: 240 }}>
                                {hasEvent && (
                                  <Chip
                                    size="sm"
                                    variant="soft"
                                    sx={{
                                      alignSelf: 'flex-start',
                                      maxWidth: '100%',
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      textTransform: 'none',
                                      bgcolor: eventStyle.bg,
                                      color: eventStyle.color,
                                    }}
                                  >
                                    <Typography
                                      level="body-xs"
                                      component="span"
                                      noWrap
                                      title={eventLabel}
                                      sx={{ fontWeight: 500, maxWidth: 220 }}
                                    >
                                      {eventLabel}
                                    </Typography>
                                  </Chip>
                                )}
                                {locationLabel && (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
                                    <LocationOnIcon sx={{ fontSize: 16, color: textMuted, mt: 0.125, flexShrink: 0 }} />
                                    <Typography
                                      level="body2"
                                      noWrap
                                      title={locationLabel}
                                      sx={{ color: textSecondary, fontSize: '0.8125rem', minWidth: 0, maxWidth: 220 }}
                                    >
                                      {locationLabel}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </td>
                          <td>{s.receiver_details ? formatDestinationLocationType(s.receiver_details.destination_location_type, t, siteName) : '—'}</td>
                          <td>
                            <Typography level="body2" sx={{ maxWidth: 220 }} noWrap title={s.receiver_details && s.receiver_details.destination_location ? s.receiver_details.destination_location : ''}>
                              {s.receiver_details && s.receiver_details.destination_location ? s.receiver_details.destination_location : '—'}
                            </Typography>
                          </td>
                          <td>
                            <Chip
                              size="sm"
                              variant="soft"
                              sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 500, textTransform: 'none' }}
                            >
                              {formatShipmentStatus(s.status, t)}
                            </Chip>
                          </td>
                          <td>
                            {s.payment_status ? (
                              <Chip
                                size="sm"
                                variant="soft"
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  textTransform: 'none',
                                  ...getPaymentStatusChipStyle(s.payment_status),
                                }}
                              >
                                {formatPaymentStatus(s.payment_status)}
                              </Chip>
                            ) : '—'}
                          </td>
                          <td>
                            {s.price != null && s.price !== '' ? <OmrAmount value={s.price} /> : '—'}
                          </td>
                          <td>{formatDate(s.transaction_date)}</td>
                          <td>{formatDate(s.created_at)}</td>
                          <td>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <IconButton
                                variant="soft"
                                size="sm"
                                color="neutral"
                                onClick={() => setQuickViewShipment(s)}
                                title={t('customer.quickViewButtonTitle')}
                                sx={{ borderRadius: '8px' }}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                              <Button
                                variant="soft"
                                size="sm"
                                color="neutral"
                                startDecorator={<LocationOnIcon />}
                                onClick={() => openSingleDeliveryModal(s)}
                                disabled={cannotUpdateDeliveryLocation(s.status)}
                                title={cannotUpdateDeliveryLocation(s.status) ? t('customer.cannotUpdateDelivery') : t('customer.updateDeliveryLocation')}
                                sx={{ borderRadius: '8px', fontWeight: 500 }}
                              >
                                {t('customer.delivery')}
                              </Button>
                              <Button
                                variant="soft"
                                color="primary"
                                size="sm"
                                startDecorator={<VisibilityIcon />}
                                onClick={() => navigate(`/customer-shipment/${s.id}/tracking`)}
                                sx={{ borderRadius: '8px', fontWeight: 500 }}
                              >
                                {t('customer.track')}
                              </Button>
                            </Box>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Sheet>

              {/* Quick view modal */}
              <Modal open={!!quickViewShipment} onClose={() => setQuickViewShipment(null)}>
                <ModalDialog
                  sx={{
                    maxWidth: 560,
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    borderRadius: cardRadius,
                    p: 2.5,
                  }}
                >
                  <ModalClose />
                  {quickViewShipment && (
                    <>
                      <Typography level="h5" sx={{ mb: 2, fontWeight: 700 }}>
                        {t('customer.shipmentNumber', { id: quickViewShipment.id })}
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {[
                          { rowKey: 'shipper_ref', label: t('customer.quickView.shipperReference'), value: quickViewShipment.shipper_reference || '—' },
                          { rowKey: 'tracking', label: t('customer.quickView.trackingNumber'), value: quickViewShipment.tracking_number || '—' },
                          ...(isCustomerShipmentUnitFieldVisible('serial_number', unitVisibleFields)
                            ? [{ rowKey: 'unit', label: t('customer.quickView.unitSerial'), value: getCustomerShipmentUnitSerial(quickViewShipment) || '—' }]
                            : []),
                          ...(isCustomerShipmentUnitFieldVisible('expected_destination_eta', unitVisibleFields) && getCustomerShipmentUnit(quickViewShipment)?.expected_destination_eta
                            ? [{ rowKey: 'dest_eta', label: t('customer.quickView.expectedDestinationEta'), value: formatCustomerShipmentUnitDate(getCustomerShipmentUnit(quickViewShipment).expected_destination_eta) }]
                            : []),
                          ...(isCustomerShipmentUnitFieldVisible('expected_warehouse_eta', unitVisibleFields) && getCustomerShipmentUnit(quickViewShipment)?.expected_warehouse_eta
                            ? [{ rowKey: 'wh_eta', label: t('customer.quickView.expectedWarehouseEta'), value: formatCustomerShipmentUnitDate(getCustomerShipmentUnit(quickViewShipment).expected_warehouse_eta) }]
                            : []),
                          { rowKey: 'items', label: t('customer.quickView.itemsDescription'), value: quickViewShipment.items_description || '—' },
                          { rowKey: 'pkg', label: t('customer.quickView.packagesQuantity'), value: quickViewShipment.packages_quantity != null ? String(quickViewShipment.packages_quantity) : '—' },
                          { rowKey: 'weight', label: t('customer.quickView.weight'), value: quickViewShipment.weight != null && quickViewShipment.weight !== '' ? String(quickViewShipment.weight) : '—' },
                          { rowKey: 'dim', label: t('customer.quickView.dimensions'), value: [quickViewShipment.width_cm, quickViewShipment.length_cm, quickViewShipment.height_cm].every((v) => v != null && v !== '') ? `${quickViewShipment.width_cm} × ${quickViewShipment.length_cm} × ${quickViewShipment.height_cm} cm` : '—' },
                          { rowKey: 'vol', label: t('customer.quickView.volumetricWeight'), value: quickViewShipment.volumetric_weight != null && quickViewShipment.volumetric_weight !== '' ? String(quickViewShipment.volumetric_weight) : '—' },
                          { rowKey: 'price', label: t('customer.quickView.price'), value: quickViewShipment.price != null && quickViewShipment.price !== '' ? <OmrAmount value={quickViewShipment.price} /> : '—' },
                          { rowKey: 'loc', label: t('customer.quickView.location'), value: quickViewShipment.location_name || (quickViewShipment.location != null && quickViewShipment.location !== '' ? String(quickViewShipment.location) : '—') },
                          { rowKey: 'status', label: t('customer.quickView.status'), value: formatShipmentStatus(quickViewShipment.status, t), kind: 'shipStatus' },
                          { rowKey: 'event', label: t('customer.quickView.eventType'), value: quickViewShipment.event_type ? t(`eventTypeCodes.${quickViewShipment.event_type}`, { defaultValue: String(quickViewShipment.event_type).replace(/_/g, ' ') }) : '—' },
                          { rowKey: 'pay', label: t('customer.quickView.paymentStatus'), value: quickViewShipment.payment_status ? formatPaymentStatus(quickViewShipment.payment_status) : '—', kind: 'payStatus' },
                          { rowKey: 'inv', label: t('customer.quickView.invoiced'), value: quickViewShipment.invoiced != null ? (quickViewShipment.invoiced ? t('actions.yes') : t('actions.no')) : '—' },
                          { rowKey: 'note', label: t('customer.quickView.note'), value: quickViewShipment.note || '—' },
                          { rowKey: 'txn', label: t('customerList.colTransactionDate'), value: formatDate(quickViewShipment.transaction_date) },
                          { rowKey: 'created', label: t('customer.quickView.created'), value: formatDateTime(quickViewShipment.created_at) },
                          { rowKey: 'mod', label: t('customer.quickView.modified'), value: formatDateTime(quickViewShipment.modified_at) },
                        ].map(({ rowKey, label, value, kind }) => {
                          const isStatus = kind === 'shipStatus';
                          const isPaymentStatus = kind === 'payStatus';
                          return (
                            <Grid key={rowKey} xs={12} sm={6}>
                              <Typography level="body2" sx={{ color: textSecondary, fontWeight: 600, mb: 0.25 }}>
                                {label}
                              </Typography>
                              {isStatus ? (
                                <Chip
                                  size="sm"
                                  variant="soft"
                                  sx={{
                                    mt: 0.25,
                                    bgcolor: shipmentStatusChipStyle(quickViewShipment.status).bg,
                                    color: shipmentStatusChipStyle(quickViewShipment.status).color,
                                    fontWeight: 500,
                                    textTransform: 'none',
                                  }}
                                >
                                  {formatShipmentStatus(quickViewShipment.status, t)}
                                </Chip>
                              ) : isPaymentStatus ? (
                                <Chip
                                  size="sm"
                                  variant="soft"
                                  sx={{
                                    mt: 0.25,
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    ...getPaymentStatusChipStyle(quickViewShipment.payment_status),
                                  }}
                                >
                                  {quickViewShipment.payment_status ? formatPaymentStatus(quickViewShipment.payment_status) : '—'}
                                </Chip>
                              ) : (
                                <Typography level="body1" sx={{ color: textPrimary }}>
                                  {value}
                                </Typography>
                              )}
                            </Grid>
                          );
                        })}
                      </Grid>
                      {quickViewShipment.receiver_details && (
                        <>
                          <Typography level="title-sm" sx={{ color: textSecondary, fontWeight: 600, mb: 1, mt: 1 }}>
                            {t('customer.receiverDetailsBlock')}
                          </Typography>
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            {[
                              { rk: 'rn', label: t('customer.receiverName'), value: quickViewShipment.receiver_details.receiver_name || '—' },
                              { rk: 'rp', label: t('customer.receiverPhone'), value: quickViewShipment.receiver_details.receiver_phone_number || '—' },
                              { rk: 'rc', label: t('customer.receiverCivilId'), value: quickViewShipment.receiver_details.receiver_civil_id || '—' },
                              { rk: 'dt', label: t('customer.destinationTypeForm'), value: formatDestinationLocationType(quickViewShipment.receiver_details.destination_location_type, t, siteName) },
                              { rk: 'dl', label: t('customer.destinationField'), value: quickViewShipment.receiver_details.destination_location || '—' },
                            ].map(({ rk, label, value }) => (
                              <Grid key={rk} xs={12} sm={6}>
                                <Typography level="body2" sx={{ color: textSecondary, fontWeight: 600, mb: 0.25 }}>
                                  {label}
                                </Typography>
                                <Typography level="body1" sx={{ color: textPrimary }}>
                                  {value}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      )}
                      {!quickViewShipment.receiver_details && (
                        <Typography level="body2" sx={{ color: textSecondary, mb: 2 }}>
                          {t('customer.noReceiverDetails')}
                        </Typography>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="plain" onClick={() => setQuickViewShipment(null)}>
                          {t('actions.close')}
                        </Button>
                        <Button
                          variant="soft"
                          color="primary"
                          startDecorator={<VisibilityIcon />}
                          onClick={() => {
                            setQuickViewShipment(null);
                            navigate(`/customer-shipment/${quickViewShipment.id}/tracking`);
                          }}
                        >
                          {t('customer.trackShipment')}
                        </Button>
                      </Box>
                    </>
                  )}
                </ModalDialog>
              </Modal>

              {/* Single shipment delivery location modal */}
              <Modal open={!!singleDeliveryShipment} onClose={() => !singleDeliverySubmitting && setSingleDeliveryShipment(null)}>
                <ModalDialog
                  sx={{
                    maxWidth: 480,
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    borderRadius: cardRadius,
                    p: 2.5,
                  }}
                >
                  <ModalClose disabled={singleDeliverySubmitting} />
                  {singleDeliveryShipment && (
                    <>
                      <Typography level="h6" sx={{ mb: 2, fontWeight: 700 }}>
                        {t('customer.deliveryLocationTitle', { id: singleDeliveryShipment.id })}
                      </Typography>
                      <Typography level="body2" sx={{ color: textSecondary, mb: 2 }}>
                        {t('customer.deliveryDescription')}
                      </Typography>
                      <CustomerDeliveryDestinationForm
                        destinationType={singleDeliveryType}
                        onDestinationTypeChange={setSingleDeliveryType}
                        destinationLocation={singleDeliveryLocation}
                        onDestinationLocationChange={setSingleDeliveryLocation}
                        siteName={siteName}
                        disabled={singleDeliverySubmitting}
                      />
                      {singleDeliveryResult && singleDeliveryResult.error && (
                        <Alert color="danger" variant="soft" sx={{ mb: 2, borderRadius: '10px' }}>
                          {singleDeliveryResult.error}
                        </Alert>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button
                          variant="plain"
                          onClick={() => !singleDeliverySubmitting && setSingleDeliveryShipment(null)}
                          disabled={singleDeliverySubmitting}
                        >
                          {t('actions.cancel')}
                        </Button>
                        <Button
                          variant="soft"
                          color="primary"
                          onClick={handleSingleDeliverySubmit}
                          loading={singleDeliverySubmitting}
                          startDecorator={<LocationOnIcon />}
                          sx={{ borderRadius: '10px' }}
                        >
                          {t('customer.updateLocation')}
                        </Button>
                      </Box>
                    </>
                  )}
                </ModalDialog>
              </Modal>

              {/* Bulk delivery location modal */}
              <Modal open={bulkReceiverModalOpen} onClose={() => !bulkReceiverSubmitting && setBulkReceiverModalOpen(false)}>
                <ModalDialog
                  sx={{
                    maxWidth: 480,
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    borderRadius: cardRadius,
                    p: 2.5,
                  }}
                >
                  <ModalClose disabled={bulkReceiverSubmitting} />
                  <Typography level="h6" sx={{ mb: 2, fontWeight: 700 }}>
                    {t('customerList.bulkDeliveryBulkTitle')}
                  </Typography>
                  <CustomerDeliveryDestinationForm
                    destinationType={bulkDestinationType}
                    onDestinationTypeChange={setBulkDestinationType}
                    destinationLocation={bulkDestinationLocation}
                    onDestinationLocationChange={setBulkDestinationLocation}
                    siteName={siteName}
                    disabled={bulkReceiverSubmitting}
                  />
                  {bulkReceiverResult && bulkReceiverResult.error && (
                    <Alert color="danger" variant="soft" sx={{ mb: 2, borderRadius: '10px' }}>
                      {bulkReceiverResult.error}
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                      variant="plain"
                      onClick={() => !bulkReceiverSubmitting && setBulkReceiverModalOpen(false)}
                      disabled={bulkReceiverSubmitting}
                    >
                      {t('actions.cancel')}
                    </Button>
                    <Button
                      variant="soft"
                      color="primary"
                      onClick={handleBulkReceiverSubmit}
                      loading={bulkReceiverSubmitting}
                      startDecorator={<LocationOnIcon />}
                      sx={{ borderRadius: '10px' }}
                    >
                      {t('customer.updateLocation')}
                    </Button>
                  </Box>
                </ModalDialog>
              </Modal>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                  <IconButton
                    variant="outlined"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    {i18n.dir() === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                  </IconButton>
                  <Typography level="body2" sx={{ color: textSecondary, px: 1 }}>
                    {t('customerList.pageOf', { page, total: totalPages })}
                  </Typography>
                  <IconButton
                    variant="outlined"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    {i18n.dir() === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      <Snackbar
        autoHideDuration={5000}
        open={!!(bulkSuccessMessage || exportError)}
        variant="soft"
        color={exportError ? 'danger' : 'success'}
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setBulkSuccessMessage(null);
          setExportError(null);
        }}
      >
        {exportError || bulkSuccessMessage}
      </Snackbar>
    </Box>
  );
}
